import React from 'react';
import { Users, Award, BookOpen, TrendingUp } from 'lucide-react';
import { teamMember } from '../mock';

export const Team = () => {
  const advisoryAreas = [
    {
      icon: Award,
      title: "Regulatory Compliance",
      description: "SEBI Guidelines & AIF Regulations"
    },
    {
      icon: TrendingUp,
      title: "Market Analysis",
      description: "IPO Due Diligence & SME Research"
    },
    {
      icon: BookOpen,
      title: "Legal & Tax",
      description: "Structuring & Documentation"
    },
    {
      icon: Users,
      title: "Investor Relations",
      description: "Fundraising & Communication"
    }
  ];

  return (
    <section id="team" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Leadership <span className="text-emerald-600">Team</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experienced professionals dedicated to delivering superior investment outcomes
          </p>
        </div>

        {/* Fund Manager Profile */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-3xl overflow-hidden shadow-md ring-2 ring-emerald-100">
                  <img
                    src="/images/shubham-gupta.jpeg"
                    alt="Shubham Gupta - Lead Fund Manager"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="team-shubham-photo"
                  />
                </div>
              </div>
              
              {/* Profile Information */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{teamMember.name}</h3>
                <p className="text-xl text-emerald-600 font-semibold mb-4">{teamMember.role}</p>
                
                <div className="space-y-3 text-gray-600 leading-relaxed">
                  <p><strong className="text-gray-900">Experience:</strong> {teamMember.experience}</p>
                  <p>
                    <strong className="text-gray-900">Specialization:</strong> 
                    <span className="inline-flex items-center ml-2 px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-semibold rounded-full border border-emerald-200">
                      📋 SEBI Registered Research Analyst
                    </span>
                    <span className="ml-2 text-gray-600">IPO investing and SME equity</span>
                  </p>
                  <p>
                    <strong className="text-gray-900">Background:</strong> Former banker and educator under the brand 
                    <a 
                      href="https://x.com/investorniti" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center ml-2 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-bold rounded-full border border-blue-200 hover:bg-blue-200 transition-colors duration-200"
                    >
                      🔗 Investorniti
                    </a>
                  </p>
                </div>
                
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">10+</div>
                    <div className="text-sm text-gray-600">Years Experience</div>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-teal-600">IPO</div>
                    <div className="text-sm text-gray-600">Specialist</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">SME</div>
                    <div className="text-sm text-gray-600">Focus</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mentor / Advisor Profile - B.B. Tewari */}
        <div className="max-w-4xl mx-auto mb-16" data-testid="team-bb-tewari-card">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-3xl overflow-hidden shadow-md ring-2 ring-emerald-100">
                  <img
                    src="/images/bb-tewari.jpeg"
                    alt="Bharat Bhoosan Tewari - Mentor & Advisor"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="team-bb-tewari-photo"
                  />
                </div>
              </div>

              {/* Profile Information */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-3xl font-bold text-gray-900 mb-2">Bharat Bhoosan Tewari</h3>
                <p className="text-xl text-emerald-600 font-semibold mb-4">Mentor &amp; Advisor</p>

                <div className="space-y-3 text-gray-600 leading-relaxed">
                  <p>
                    B.B. Tewari is a <strong className="text-gray-900">strategic advisor, investor, and consultant to corporates and startups</strong>,
                    who also champions social and environmental initiatives focused on
                    <strong className="text-gray-900"> responsible citizenship, sustainability, and awareness programs</strong>.
                    He is associated with global civic platforms like
                    <span className="inline-flex items-center mx-1 px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-semibold rounded-full border border-emerald-200">
                      WCPA
                    </span>
                    and works towards youth engagement and positive social change.
                  </p>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">ESG</div>
                    <div className="text-sm text-gray-600">Sustainability</div>
                  </div>
                  <div className="bg-teal-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-teal-600">WCPA</div>
                    <div className="text-sm text-gray-600">Civic Platform</div>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-emerald-600">Youth</div>
                    <div className="text-sm text-gray-600">Engagement</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Advisory Panel */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Advisory Expertise</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Distinguished panel of experts providing guidance across critical areas
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advisoryAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{area.title}</h4>
                  <p className="text-sm text-gray-600">{area.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Expansion */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl p-8 md:p-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Growing Team</h3>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            We are actively expanding our team with top talent across investment analysis, compliance, risk management, and investor relations to ensure exceptional service delivery.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <TrendingUp className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Investment Analysts</h4>
              <p className="text-sm text-gray-600">Financial due diligence & sector research</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <Award className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Compliance Officers</h4>
              <p className="text-sm text-gray-600">Risk management & regulatory oversight</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <Users className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
              <h4 className="font-semibold text-gray-900 mb-2">Operations Team</h4>
              <p className="text-sm text-gray-600">Investor relations & operations</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};